<template>
  <span class="vueContainer">
    <div class="filter-container">
      <div class="search-row">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search..."
          @keyup.enter="applySearch"
        />
        <button @click="applySearch">Search</button>
        <button class="clear-all-btn" @click="clearAll">Clear all</button>
      </div>
      <div class="filters-row" v-click-outside="closeDropdowns">
        <div
          class="filter-column"
          v-for="(filter, index) in filters"
          :key="index"
        >
          <label class="filter-title">{{ filter.title }}</label>
          <div
            ref="toggleButton"
            @click="toggleDropdown(index)"
            class="dropdown-button"
          >
            <span class="NofFilter" v-show="selectedFiltersActive[index] > 0">{{
              selectedFiltersActive[index]
            }}</span>
            Select filters...
            <span class="arrow">&#9660;</span>
          </div>
          <div class="checkbox-dropdown" :class="{ show: filter.show }">
            <div
              class="checkbox-option"
              v-for="option in filter.options"
              :key="option.name"
            >
              <input
                type="checkbox"
                :id="option.name"
                :value="option.name"
                v-model="option.checked"
                @change="onFilterChange"
              />
              <label :for="option.name">{{ option.name }}</label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Section -->
    <div class="results-container">
      <div class="lg-grid">
        <article
          v-for="(post, index) in visibleResults"
          :key="post.id"
          class="lg-card"
          :class="familyClass(post)"
        >
          <!-- top color bar -->
          <div class="lg-card__head"></div>

          <!-- body: content at top, badge bottom-right (matches library-grid markup) -->
          <div class="lg-card__body">
            <div class="lg-card__content">
              <h3 class="lg-card__title" v-html="post.title.rendered"></h3>
              <p class="lg-card__excerpt">{{ excerptText(post) }}</p>
            </div>
            <div
              class="lg-card__badge"
              aria-hidden="true"
              v-if="getIconSrc(post)"
            >
              <img
                class="lg-card__badge-img"
                :src="getIconSrc(post)"
                alt=""
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <!-- full-card link overlay -->
          <a
            class="lg-card__link"
            :href="post.link"
            :aria-label="stripHtml(post.title.rendered)"
          ></a>
        </article>

        <div v-if="!firstLoadDone" class="loaderContainer">
          <div class="loader"></div>
        </div>
        <div v-else-if="visibleResults.length < 1">
          No results found based on your filter selection
        </div>
      </div>
    </div>

    <!-- Load more button -->
    <div
      v-if="
        firstLoadDone &&
          filteredResults.length > visibleResults.length &&
          visibleResults.length > 1
      "
      class="lg-actions"
    >
      <button class="lg-loadmore" @click="loadMoreResults">Load More</button>
    </div>
  </span>
</template>

<script>
const _contentUrl = (typeof wpData !== 'undefined' && wpData.content_url)
    ? wpData.content_url.replace(/\/$/, '')
    : '/app';

export default {
  data() {
    return {
      // VERSION LOG - to verify cache is cleared
      componentVersion: "2026-07-16",

      firstLoadDone: false,

      visibleResults: [],
      filteredResults: [],

      allPosts: [],
      fullDatabase: [],

      selectedFiltersActive: [0, 0, 0, 0],

      itemsPerPage: 9,

      searchQuery: "",

      // ✅ UPDATED FILTERS with correct taxonomy term IDs
      filters: [
        {
          title: "Support for Members",
          show: false,
          selectedFiltersActive: "",
          options: [
            { name: "Clinic Business Solutions", ids: [339] },
            { name: "Division Membership", ids: [340] },
            { name: "Medical Office Assistants (MOA)", ids: [341] },
            { name: "Member Wellness", ids: [342] },
            { name: "Patient Attachment Initiative (PAI)", ids: [343] },
            { name: "Patient Medical Homes (PMH)", ids: [344] },
            { name: "Primary Care Networks (PCN)", ids: [345] },
            { name: "Recruitment & Retention", ids: [346] }
          ]
        },
        {
          title: "Special Topics",
          show: false,
          selectedFiltersActive: "",
          options: [
            { name: "Children & Youth", ids: [347] },
            { name: "Clinical Advisories", ids: [348] },
            { name: "Communities of Practice", ids: [349] },
            { name: "Continuity of Care", ids: [350] },
            { name: "Doc to Doc Series", ids: [351] },
            { name: "Environment & Sustainability", ids: [352] },
            { name: "Grants & Funding", ids: [353] },
            { name: "Interprofessional Teams (IPT)", ids: [354] },
            {
              name: "Justice, Equity, Diversity & Inclusion (JEDI)",
              ids: [355]
            },
            { name: "Maternity", ids: [356] },
            { name: "Mental Health", ids: [357] },
            { name: "Nurses", ids: [358] },
            { name: "Peer Support", ids: [359] },
            { name: "Pharmacists", ids: [360] },
            { name: "Seniors & Long Term Care", ids: [361] },
            { name: "Vancouver Division AGM", ids: [362] }
          ]
        },
        {
          title: "Content Type",
          show: false,
          selectedFiltersActive: "",
          options: [
            { name: "Event", ids: [290] },
            { name: "Fast Facts Issues", ids: [291] },
            { name: "Initiative", ids: [292] },
            { name: "News Article", ids: [293] },
            { name: "Tools & Resources", ids: [294] },
            { name: "Web Page", ids: [], format: "page" }
          ]
        },
        {
          title: "Custom Tags",
          show: false,
          selectedFiltersActive: "",
          options: [
            { name: "AGM", ids: [330] },
            { name: "Fast Facts", ids: [332] },
            { name: "PCN 1", ids: [333] },
            { name: "PCN 2", ids: [334] },
            { name: "PCN 3", ids: [335] },
            { name: "PCN 4", ids: [336] },
            { name: "PCN 5", ids: [337] },
            { name: "PCN 6", ids: [338] }
          ]
        }
      ]
    };
  },

  methods: {
    nameToSlug(name) {
      return name
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();
    },

    filterTitleToKey(filterTitle) {
      const keyMap = {
        "Support for Members": "support",
        "Special Topics": "topics",
        "Content Type": "content",
        "Custom Tags": "tag"
      };
      return (
        keyMap[filterTitle] || filterTitle.toLowerCase().replace(/\s+/g, "-")
      );
    },

    keyToFilterTitle(key) {
      const titleMap = {
        support: "Support for Members",
        topics: "Special Topics",
        content: "Content Type",
        tag: "Custom Tags"
      };
      return titleMap[key] || null;
    },

    buildFriendlyUrl() {
      const baseUrl = "/our-division/search/";
      const term = (this.searchQuery || "").trim();

      const queryParams = [];

      this.filters.forEach(filter => {
        const key = this.filterTitleToKey(filter.title);
        const selectedOptions = filter.options
          .filter(opt => opt.checked)
          .map(opt => this.nameToSlug(opt.name));

        if (selectedOptions.length > 0) {
          queryParams.push(`${key}=${selectedOptions.join(",")}`);
        }
      });

      let url = baseUrl;
      if (term !== "") {
        url += "q/" + encodeURIComponent(term);
      }

      if (queryParams.length > 0) {
        url += "?" + queryParams.join("&");
      }

      return url;
    },

    extractSearchQuery() {
      const path = window.location.pathname.replace(/\/+$/, "").toLowerCase();

      const parts = path.split("/").filter(Boolean);
      const searchIndex = parts.indexOf("search");

      if (searchIndex === -1) return "";

      if (parts[searchIndex + 1] === "q" && parts[searchIndex + 2]) {
        try {
          return decodeURIComponent(parts[searchIndex + 2]);
        } catch (e) {
          return parts[searchIndex + 2];
        }
      }

      return "";
    },

    extractFiltersFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const extractedFilters = {};

      for (const [key, value] of params.entries()) {
        const filterTitle = this.keyToFilterTitle(key);
        if (!filterTitle) continue;

        const slugs = value.split(",");
        extractedFilters[filterTitle] = slugs;
      }

      return extractedFilters;
    },

    applyFriendlyUrlFilters() {
      const extractedFilters = this.extractFiltersFromUrl();

      this.filters.forEach(filter => {
        filter.options.forEach(option => {
          option.checked = false;
        });
      });

      this.filters.forEach(filter => {
        if (extractedFilters[filter.title]) {
          const slugsToCheck = extractedFilters[filter.title];
          filter.options.forEach(option => {
            if (slugsToCheck.includes(this.nameToSlug(option.name))) {
              option.checked = true;
            }
          });
        }
      });

      this.updateFilterCounts();
    },

    updateFilterCounts() {
      this.selectedFiltersActive = this.filters.map(
        filter => filter.options.filter(opt => opt.checked).length
      );
    },

    onFilterChange() {
      const newUrl = this.buildFriendlyUrl();
      window.history.replaceState({}, "", newUrl);
      this.applyCompositeFilter();
    },

    clearAll() {
      this.filters.forEach(f => {
        f.options.forEach(opt => {
          opt.checked = false;
        });
        f.show = false;
      });

      this.selectedFiltersActive = this.selectedFiltersActive.map(() => 0);
      this.searchQuery = "";

      window.history.replaceState({}, "", "/our-division/search/");

      this.applyCompositeFilter();
    },

    stripHtml(html) {
      if (!html) return "";
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return (tmp.textContent || tmp.innerText || "").trim();
    },

    trimWords(text, words = 26, ellipsis = "…") {
      const parts = text.split(/\s+/).filter(Boolean);
      if (parts.length <= words) return text;
      return parts.slice(0, words).join(" ") + ellipsis;
    },

    excerptText(post) {
      let txt = "";

      const hasAcfExcerpt =
        post &&
        post.acf &&
        typeof post.acf["post-excerpt"] === "string" &&
        post.acf["post-excerpt"].trim();

      if (hasAcfExcerpt) {
        txt = this.stripHtml(post.acf["post-excerpt"]);
      } else if (post && post.excerpt && post.excerpt.rendered) {
        txt = this.stripHtml(post.excerpt.rendered);
      } else if (post && post.content && post.content.rendered) {
        txt = this.stripHtml(post.content.rendered);
      }

      txt = String(txt)
        .replace(/\s+/g, " ")
        .trim();
      return this.trimWords(txt, 26, "…");
    },

    familyClass(post) {
      const pt = post && post.type ? String(post.type).toLowerCase() : "";
      if (pt === "initiative") return "lg-card--initiatives";
      if (pt === "event") return "lg-card--events";
      if (["resource", "tool", "report"].includes(pt))
        return "lg-card--resources";

      const ct =
        post && Array.isArray(post.content_type) ? post.content_type : [];

      const isEvent = this.hasAny(ct, [290]);
      const isInitiative = this.hasAny(ct, [292]);
      const isResource = this.hasAny(ct, [291, 293, 294, 295]);

      if (isInitiative) return "lg-card--initiatives";
      if (isEvent) return "lg-card--events";
      return "lg-card--resources";
    },

    hasAny(arr, ids) {
      if (
        !Array.isArray(arr) ||
        !Array.isArray(ids) ||
        !arr.length ||
        !ids.length
      )
        return false;
      const set = new Set(arr.map(Number));
      return ids.some(id => set.has(Number(id)));
    },

    toggleDropdown(index) {
      this.filters.forEach((filter, i) => {
        if (i !== index) filter.show = false;
      });
      this.filters[index].show = !this.filters[index].show;
    },

    getIconSrc(post) {
      return post.vdfp_icon_url || "";
    },

    async getAllPosts(postType, page) {
      try {
        // ✅ FIXED: Use KEBAB-CASE field names from REST API
        const response = await fetch(
          `/wp-json/wp/v2/${postType}?page=${page}&per_page=100&_fields=id,title,excerpt,link,acf,date,vdfp_icon_url,support-for-members,special-topics,content-type-v2,tags-secondary`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const posts = await response.json();
        const totalPages = parseInt(response.headers.get("X-WP-TotalPages"));

        posts.forEach((post, idx) => {
          post.formatType = "post";

          // ✅ MAP REST API TAXONOMY FIELDS
          // The API returns kebab-case field names with arrays of IDs (or objects with id property)
          post.tx_tags = this.extractIds(post["tags-secondary"]);
          post.content_type = this.extractIds(post["content-type-v2"]);
          post.programs_committees = this.extractIds(
            post["support-for-members"]
          );
          post.special_topics = this.extractIds(post["special-topics"]);
        });

        this.allPosts = this.allPosts.concat(posts);

        if (page < totalPages) {
          return this.getAllPosts(postType, page + 1);
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    },

    extractIds(arr) {
      if (!Array.isArray(arr)) return [];
      return arr.map(item => {
        // Handle both plain IDs and objects with id property
        return typeof item === "object" && item.id ? item.id : item;
      });
    },

    async getAllPages() {
      let allPages = [];
      const perPage = 100;
      // ✅ Pages only have ACF data, not REST API taxonomies
      const fields =
        "id,title,excerpt,link,acf,date,vdfp_icon_url,vdfp_manual_excerpt";

      const fetchPages = async (page = 1, totalPages = 0) => {
        try {
          const response = await fetch(
            `/wp-json/wp/v2/pages?page=${page}&per_page=${perPage}&_fields=${fields}`
          );

          if (!response.ok) {
            throw new Error(`Network response was not ok for page ${page}`);
          }

          const contentType = response.headers.get("Content-Type");
          if (!contentType || !contentType.includes("application/json")) {
            console.warn(
              `Expected JSON response for page ${page}, but received: ${contentType}`
            );
            throw new Error(`Unexpected content type for page ${page}`);
          }

          if (page === 1) {
            totalPages = parseInt(response.headers.get("X-WP-TotalPages"), 10);
          }

          const data = await response.json();
          allPages = allPages.concat(data);

          if (page < totalPages) {
            await fetchPages(page + 1, totalPages);
          } else {
            let relevantPages = [];
            allPages.forEach((item, idx) => {
              const manualExcerpt =
                typeof item.vdfp_manual_excerpt === "string"
                  ? item.vdfp_manual_excerpt.trim()
                  : "";
              if (manualExcerpt === "") {
                // skip pages without a hand-written excerpt — the rendered
                // excerpt auto-generates from content and is not a curation
                // signal (search surfaces curated pages only)
              } else {
                item.acf["post-excerpt"] = manualExcerpt;

                // ✅ PAGES: Use only ACF data (no REST API taxonomies for pages)
                item.tx_tags = item.acf.tag
                  ? this.extractIds(item.acf.tag)
                  : [];
                item.content_type = item.acf.content_type
                  ? this.extractIds(item.acf.content_type)
                  : [];
                item.special_topics = item.acf.special_topics
                  ? this.extractIds(item.acf.special_topics)
                  : [];
                item.programs_committees = item.acf.program_comites
                  ? this.extractIds(item.acf.program_comites)
                  : [];

                item.formatType = "page";
                relevantPages.push(item);
              }
            });
            this.allPosts = this.allPosts.concat(relevantPages);
          }
        } catch (error) {
          console.error("Error fetching page:", page, error);
        }
      };

      await fetchPages();
    },

    fetchAllPosts() {
      return Promise.all([
        this.getAllPosts("event", 1),
        this.getAllPosts("initiative", 1),
        this.getAllPosts("resource", 1),
        this.getAllPages()
      ])
        .then(() => {
          this.allPosts.sort(
            (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
          );
          this.firstLoadDone = true;
          this.fullDatabase = this.allPosts;
          this.filteredResults = this.allPosts;
          this.visibleResults = this.fullDatabase.slice(0, this.itemsPerPage);

          // Database loaded successfully
          console.log("Database loaded");
        })
        .catch(error => console.error("Error fetching posts:", error));
    },

    loadMoreResults() {
      const totalVisible = this.visibleResults.length;
      const nextItems = this.filteredResults.slice(
        totalVisible,
        totalVisible + this.itemsPerPage
      );
      this.visibleResults = [...this.visibleResults, ...nextItems];
    },

    applySearch() {
      const newUrl = this.buildFriendlyUrl();
      window.history.replaceState({}, "", newUrl);
      this.applySearchInternal();
    },

    applySearchInternal() {
      this.applyCompositeFilter();
    },

    applyCompositeFilter() {
      // Count selected filters in each category
      this.selectedFiltersActive = this.filters.map(
        filter => filter.options.filter(opt => opt.checked).length
      );

      // Collect ALL selected filter IDs (and virtual format matchers) across all categories
      const allSelectedIds = [];
      const selectedFormats = [];

      this.filters.forEach(filter => {
        filter.options.forEach(option => {
          if (option.checked) {
            allSelectedIds.push(...option.ids);
            if (option.format) {
              selectedFormats.push(option.format);
            }
          }
        });
      });

      // If no filters and no search, show everything
      if (
        allSelectedIds.length === 0 &&
        selectedFormats.length === 0 &&
        !this.searchQuery
      ) {
        return (this.visibleResults = this.fullDatabase);
      }

      // Filter posts - show if it matches ANY selected ID from ANY category,
      // or any virtual format matcher (e.g. "Web Page" -> formatType "page")
      const filteredPosts = this.fullDatabase.filter(post => {
        if (allSelectedIds.length > 0 || selectedFormats.length > 0) {
          // Get all taxonomy IDs from this post
          const postAllIds = [
            ...(post.programs_committees || []),
            ...(post.special_topics || []),
            ...(post.content_type || []),
            ...(post.tx_tags || [])
          ];

          // Show post if it has ANY of the selected IDs or a selected format
          return (
            allSelectedIds.some(id => postAllIds.includes(id)) ||
            selectedFormats.includes(post.formatType)
          );
        }

        return true;
      });

      // Apply text search
      return this.textBarSearch(filteredPosts);
    },

    postSearchableText(post) {
      let parts = [];
      if (post.title && post.title.rendered) parts.push(post.title.rendered);
      if (post.acf) {
        if (post.acf["post-excerpt"]) parts.push(post.acf["post-excerpt"]);
        if (post.acf["resource-text"]) parts.push(post.acf["resource-text"]);
      }
      if (post.excerpt && post.excerpt.rendered) parts.push(post.excerpt.rendered);
      return this.stripHtml(parts.join(" ")).toLowerCase();
    },

    textBarSearch(filteredPosts) {
      if (
        filteredPosts.length < 1 &&
        this.selectedFiltersActive.some(value => value !== 0)
      ) {
        return (this.visibleResults = []);
      }

      if (filteredPosts.length < 1) {
        filteredPosts = this.fullDatabase;
      }

      if (this.searchQuery) {
        let searchTerms = this.searchQuery.toLowerCase().split(/\s+/);
        let titleMatches = [];
        let contentMatches = [];

        filteredPosts.forEach(post => {
          let title = post.title.rendered
            ? post.title.rendered.toLowerCase()
            : "";
          let titleHit = searchTerms.every(term => title.includes(term));

          if (titleHit) {
            titleMatches.push(post);
          } else {
            let fullText = this.postSearchableText(post);
            if (searchTerms.every(term => fullText.includes(term))) {
              contentMatches.push(post);
            }
          }
        });

        filteredPosts = titleMatches.concat(contentMatches);
      }

      this.filteredResults = filteredPosts;
      const visible = filteredPosts.slice(0, this.itemsPerPage);

      return (this.visibleResults = visible);
    },

    closeDropdowns() {
      this.filters.forEach(filter => {
        filter.show = false;
      });
    }
  },

  mounted() {
    this.fetchAllPosts().then(() => {
      const query = this.extractSearchQuery();

      if (query) {
        this.searchQuery = query;
      }

      this.applyFriendlyUrlFilters();

      const hasActiveFilters = this.filters.some(f =>
        f.options.some(o => o.checked)
      );

      if ((query && query.trim() !== "") || hasActiveFilters) {
        this.applySearchInternal();
        return;
      }

      this.filteredResults = this.fullDatabase;
      this.visibleResults = this.fullDatabase.slice(0, this.itemsPerPage);
    });
  },

  directives: {
    clickOutside: {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el == event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.addEventListener("click", el.clickOutsideEvent);
      },
      unbind(el) {
        document.removeEventListener("click", el.clickOutsideEvent);
      }
    }
  }
};
</script>

<style>
.vueContainer {
  display: flex;
  flex-direction: column;
  align-content: center;
  align-items: center;
}

.filter-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.search-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
}

.search-row input {
  border-radius: 0px;
  border-color: #cccc;
}

.search-row button {
  background-color: #016d89;
  border-radius: 0px;
  transition: 0.5s;
  color: white;
  border-color: #016d89;
  margin-left: 5px;
}

.search-row button:hover {
  background-color: #cd6c33;
  border-color: #cd6c33;
}

.filters-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
}

.filter-column {
  margin-bottom: 20px;
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: start;
}

.filter-title {
  color: #016d89;
  font-family: "Barlow", Sans-serif;
  font-size: 17px;
  font-weight: 400;
  text-align: left;
  margin-bottom: 5px;
  margin-top: 10px;
}

.dropdown-button {
  background-color: #f9f9f9;
  border: 1px solid #016d89;
  color: #016d89;
  padding: 8px 16px;
  font-size: 16px;
  display: flex;
  text-align: left;
  width: 100%;
  cursor: pointer;
  position: relative;
  transition: 0.5s;
  align-content: center;
  justify-content: flex-start;
  align-items: center;
}

.dropdown-button:hover {
  border: 1px solid #cd6c33;
  background-color: #f9f9f9;
  color: #016d89;
}

.dropdown-button .arrow {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: none;
}

.checkbox-dropdown {
  display: none;
  border: 1px solid #ccc;
  position: absolute;
  background-color: #fff;
  width: 100%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.checkbox-dropdown.show {
  display: flex;
  flex-direction: column;
  width: 200px;
  margin-top: 79px;
}

.checkbox-option {
  padding: 10px 16px;
  display: flex;
  align-items: center;
}

.checkbox-option:last-child {
  border-bottom: none;
}

.checkbox-option input[type="checkbox"] {
  margin-right: 8px;
}

.checkbox-option label {
  font-family: "Barlow", Sans-serif;
  font-weight: 500;
  color: #016d89;
}

/* 640px = site-wide mobile breakpoint (matches library-grid) */
@media (max-width: 640px) {
  .filters-row {
    flex-direction: column;
  }

  .filter-column,
  .search-row {
    width: 100%;
  }
}

.results-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  margin-top: 20px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.NofFilter {
  background-color: #016d89;
  padding: 9px;
  margin-right: 5px;
  border-radius: 50%;
  color: white;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  flex-wrap: wrap;
}

.loaderContainer {
  display: flex;
  justify-content: center;
  width: 100%;
}

.loader {
  border: 10px solid #f3f3f3;
  border-top: 10px solid #016d89;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 2s linear infinite;
  margin-top: 50px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Column counts (and their 640/900 breakpoints) come from the library-grid
   stylesheet (blocks/library-grid/style.css) — do not redeclare them here. */
.results-container .lg-grid {
  gap: 24px;
  margin-left: auto;
  margin-right: auto;
}

.filter-container input,
.filter-container .dropdown-button,
.filter-container button,
.lg-loadmore {
  border-radius: 8px !important;
}

.vueContainer .lg-loadmore {
  margin-top: 20px;
}

.clear-all-btn {
  background-color: #3d91a7 !important;
  border-color: #3d91a7 !important;
}
</style>



// WEBPACK FOOTER //
// src/allResources.vue